/**
 * Build-time configuration
 *
 * This file is processed at build time with different values for:
 * - Production build (for merchants)
 * - Internal build (for team development)
 */

/**
 * Build mode - replaced at build time
 */
declare const __BUILD_MODE__: 'production' | 'internal';

import { SDK_VERSION } from './version';

/**
 * Configuration interface
 */
export interface BuildConfig {
  /** SDK version */
  version: string;

  /** Build mode */
  mode: 'production' | 'internal';

  /** Default checkout origin (production URL) */
  defaultCheckoutOrigin: string;

  /** Default Paypercut API origin */
  defaultApiOrigin: string;

  /** Default vault provider API origin */
  defaultVaultApiUrl: string;

  /** Allowed origins for postMessage validation */
  allowedOrigins: string[];

  /** Whether hostedCheckoutUrl override is allowed */
  allowOriginOverride: boolean;
}

/**
 * Production configuration (for merchants)
 */
const productionConfig: BuildConfig = {
  version: SDK_VERSION,
  mode: 'production',
  defaultCheckoutOrigin: 'https://buy.paypercut.io',
  defaultApiOrigin: 'https://api.paypercut.io',
  defaultVaultApiUrl: 'https://api.basistheory.com',
  allowedOrigins: [
    'https://buy.paypercut.io',
  ],
  allowOriginOverride: false,
};

/**
 * Internal configuration (for team development)
 */
const internalConfig: BuildConfig = {
  version: SDK_VERSION,
  mode: 'internal',
  defaultCheckoutOrigin: 'https://buy.paypercut.io',
  defaultApiOrigin: 'https://api.paypercut.io',
  defaultVaultApiUrl: 'https://api.basistheory.com',
  allowedOrigins: [
    'https://buy.paypercut.io',
    'https://buy.dev.paypercut.net',
    'https://buy.stage.paypercut.net',
    'https://demo.dev.paypercut.net',
    'https://demo.stage.paypercut.net',
    'http://localhost:3000',
    'http://localhost:3001',
    'http://localhost:31002',
    'http://127.0.0.1:3000',
    'http://127.0.0.1:3001',
    'http://127.0.0.1:31002',
  ],
  allowOriginOverride: true,
};

/**
 * Active configuration (selected at build time)
 */
export const config: BuildConfig = __BUILD_MODE__ === 'internal'
  ? internalConfig
  : productionConfig;

/**
 * Helper to check if origin is allowed
 */
export function isOriginAllowed(origin: string): boolean {
  if (config.allowedOrigins.includes(origin)) return true;
  if (config.mode !== 'internal') return false;

  try {
    const url = new URL(origin);
    if (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
    ) {
      return true;
    }
    return (
      url.protocol === 'https:' &&
      (url.hostname.endsWith('.trycloudflare.com') ||
        url.hostname.endsWith('.ngrok-free.dev') ||
        url.hostname.endsWith('.ngrok.app'))
    );
  } catch {
    return false;
  }
}

/**
 * Get the checkout origin (with optional override for internal builds)
 */
export function getCheckoutOrigin(override?: string): string {
  if (override && config.allowOriginOverride) {
    return override;
  }
  return config.defaultCheckoutOrigin;
}

/**
 * Get the Paypercut API origin (with optional override for internal builds)
 */
export function getApiOrigin(override?: string): string {
  if (override && config.allowOriginOverride) {
    return override;
  }
  return config.defaultApiOrigin;
}

/**
 * Get the vault provider API URL (not overridable — internal use only)
 */
export function getVaultApiUrl(): string {
  return config.defaultVaultApiUrl;
}
